import { useEffect, useRef, useState } from "react";
import { ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { getScrollContainer, scrollContainerToTop } from "@/lib/scroll-container";

interface BackToTopPillProps {
  /** Pixel offset from top before pill becomes visible. Default: 60vh. */
  threshold?: number;
  className?: string;
}

/**
 * Floating pill, fixed bottom-right. Fades in when the page is scrolled past
 * `threshold`. Click smoothly returns to the top of the scroll container.
 *
 * The actual scroll surface in this app is `<SidebarInset>` (overflow-y-auto
 * + h-dvh), not `window` — `getScrollContainer` finds it via its
 * `data-sidebar="inset"` attribute. Listening to window scroll instead would
 * silently never fire.
 *
 * Respects `prefers-reduced-motion` (jumps instead of animating).
 */
export function BackToTopPill({ threshold, className }: BackToTopPillProps) {
  const containerRef = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const container = getScrollContainer(null);
    containerRef.current = container;
    if (!container) return;

    const cutoff = threshold ?? Math.max(container.clientHeight * 0.6, 320);
    const onScroll = () => setVisible(container.scrollTop > cutoff);
    onScroll();
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => container.removeEventListener("scroll", onScroll);
  }, [threshold]);

  return (
    <button
      type="button"
      onClick={() => scrollContainerToTop(containerRef.current)}
      aria-label="Back to top"
      data-visible={visible || undefined}
      className={cn(
        "fixed right-6 bottom-6 z-30 h-11 w-11 rounded-full",
        "bg-background/90 supports-[backdrop-filter]:bg-background/70 backdrop-blur",
        "border border-border/60 shadow-md",
        "flex items-center justify-center",
        "text-muted-foreground",
        // Brand-purple hover state — colour, border, and a soft halo glow
        "hover:text-threadheads-purple hover:border-threadheads-purple/50 hover:bg-threadheads-purple/5",
        "hover:shadow-[0_0_18px_-4px_rgb(126_105_171_/_0.55)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-threadheads-purple focus-visible:ring-offset-2",
        "transition-all duration-200 motion-reduce:transition-none",
        "opacity-0 translate-y-2 pointer-events-none",
        "data-[visible]:opacity-100 data-[visible]:translate-y-0 data-[visible]:pointer-events-auto",
        "[touch-action:manipulation]",
        "[padding-bottom:env(safe-area-inset-bottom)]",
        className,
      )}
    >
      <ChevronUp className="h-4 w-4" aria-hidden="true" />
    </button>
  );
}
